import {
  html,
  LitElement,
  customElement,
  property,
  state,
} from '@oskecare/fe-utilities/lib/LitElement/index.js';
import { bound } from '@oskecare/fe-utilities/lib/decorators';
import { messageBus, tagManager } from '../infrastructure.js';

/**
 * BreadcrumbsBar component.
 */
@customElement('osk-breadcrumbs-bar')
class BreadcrumbsBar extends LitElement {
  /**
   * @type {Breadcrumb[]}
   */
  @property({ attribute: false })
  accessor breadcrumbs = [];

  /**
   * @type {Map<string, string>}
   */
  @state()
  accessor _placeholders = new Map();

  /**
   * @type {Map<string, string>}
   */
  @state()
  accessor _query = new Map();

  /**
   * @type {boolean}
   */
  @state()
  accessor _isLoading = false;

  /**
   * Handler for breadcrumb item click
   *
   * @param {string} path
   * @param {string} name
   * @returns {function(Event): void}
   */
  static clickHandler(path, name) {
    return event => {
      event.preventDefault();

      messageBus.publish('router', 'navigate', { path });
      tagManager.mhubPush({
        event: 'action.clickNavigation.eCare',
        action: {
          clickNavigation: {
            type: 'Breadcrumb',
            button: name,
          },
        },
      });
    };
  }

  /**
   * Replaces placeholders in breadcrumb with values from placeholdersMap object.
   *
   * @param {Breadcrumb} breadcrumb
   * @returns {string}
   */
  printBreadcrumbName(breadcrumb) {
    if (!breadcrumb.hasPlaceholders) {
      return breadcrumb.name;
    }
    let printedName = breadcrumb.name;
    this._placeholders.forEach((value, key) => {
      if (breadcrumb.placeholders.includes(key)) {
        printedName = printedName.replace(`[${key}]`, value);
      }
    });
    return printedName;
  }

  /**
   * Sets link query params for given breadcrumbs link, if any are provided by placeholdersMap object.
   *
   * @param {Breadcrumb} breadcrumb
   * @returns {string}
   */
  printBreadcrumbLink(breadcrumb) {
    if (!breadcrumb.dynamicQuery || !this._query.size) {
      return breadcrumb.link;
    }

    const url = new URL(breadcrumb.link, window.location.origin);
    this._query.forEach((value, key) => {
      if (breadcrumb.dynamicQuery.includes(key)) {
        url.searchParams.set(key, value);
      }
    });

    return `${url.pathname}?${url.searchParams}`;
  }

  /**
   * Sets data containing breadcrumbs placeholder values and turns off loading state.
   *
   * @param {?object} placeholders
   * @param {?object} query
   */
  @bound
  handleSetData({ placeholders = {}, query = {} }) {
    this._placeholders = new Map(Object.entries(placeholders));
    this._query = new Map(Object.entries(query));
    this._isLoading = false;
  }

  /**
   * Disables shadowRoot for this element.
   */
  createRenderRoot() {
    return this;
  }

  /**
   * Lifecycle callback in the custom elements API
   * Fires each time a custom element is appended into a document-connected element
   *
   * Subscribes to `messageBus` `router` `setBreadcrumbsData`
   */
  connectedCallback() {
    if (super.connectedCallback) {
      super.connectedCallback();
    }
    messageBus.subscribe('router', 'setBreadcrumbsData', this.handleSetData);
  }

  /**
   * Lifecycle callback in the custom elements API
   * Fires each time a custom element is removed from a document-connected element
   *
   * Unsubscribes from `messageBus` `router` `setBreadcrumbsData`
   */
  disconnectedCallback() {
    messageBus.unsubscribe('router', 'setBreadcrumbsData', this.handleSetData);
    if (super.disconnectedCallback) {
      super.disconnectedCallback();
    }
  }

  /**
   * Lifecycle method.
   * Called when the element’s DOM has been updated and rendered.
   *
   * @param {Map<string, string>} changedProperties - Keys are the names of changed properties; Values are the corresponding previous values.
   */
  updated(changedProperties) {
    super.updated(changedProperties);
    if (
      changedProperties.has('breadcrumbs') &&
      this.breadcrumbs.some(breadcrumb => breadcrumb.hasPlaceholders)
    ) {
      this._isLoading = true;
    }
  }

  render() {
    return this.breadcrumbs.length
      ? html`
          <nav aria-label="Omrvinková navigácia" class="osk-breadcrumbs osk-breadcrumbs--dark">
            <ol class="osk-breadcrumbs__container">
              ${this.breadcrumbs.map((breadcrumb, index) =>
                index === this.breadcrumbs.length - 1
                  ? this.renderActiveItem(breadcrumb)
                  : this.renderItem(breadcrumb),
              )}
            </ol>
          </nav>
        `
      : null;
  }

  /**
   * @param {Breadcrumb} breadcrumb
   * @returns {TemplateResult}
   */
  renderItem(breadcrumb) {
    if (breadcrumb.hasPlaceholders && this._isLoading) {
      return BreadcrumbsBar.renderLoadingItem(false);
    }
    const breadcrumbLink = this.printBreadcrumbLink(breadcrumb);
    return html`
      <li class="osk-breadcrumbs__item">
        <a
          class="osk-breadcrumbs__link"
          @click=${BreadcrumbsBar.clickHandler(breadcrumbLink, breadcrumb.name)}
          href=${breadcrumbLink}
        >
          ${this.printBreadcrumbName(breadcrumb)}
        </a>
      </li>
    `;
  }

  /**
   * @param {Breadcrumb} breadcrumb
   * @returns {TemplateResult}
   */
  renderActiveItem(breadcrumb) {
    if (breadcrumb.hasPlaceholders && this._isLoading) {
      return BreadcrumbsBar.renderLoadingItem(true);
    }
    return html`
      <li class="osk-breadcrumbs__item is-active" aria-current="page">
        ${this.printBreadcrumbName(breadcrumb)}
      </li>
    `;
  }

  /**
   * @param {boolean} isActive
   * @returns {TemplateResult}
   */
  static renderLoadingItem(isActive) {
    return html`
      <li
        class=${isActive ? 'osk-breadcrumbs__item is-active' : 'osk-breadcrumbs__item'}
        aria-current="page"
      >
        <div class="d-inline-block" style="width: 100px">
          <div class="skeleton">&zwnj;</div>
        </div>
      </li>
    `;
  }
}

export default BreadcrumbsBar;
